import { EngineConfigStatus } from '@/store/Engine/constants';
import { EngineStore } from '@/store/Engine/EngineStore';
import isEmpty from 'lodash/isEmpty';
import { versionTabs } from './Engine/CentrifugalCompressors/CentrifugalCompressors.utils';
import isNil from 'lodash/isNil';

interface TabHandlerConfig {
  condition: boolean;
  action: () => Promise<void>;
}

export const initializeEngineConfig = async (engineStore: EngineStore, tvalue) => {
  engineStore.clearEngineConfig();
  await engineStore.prepareDefaultStrategy(false, tvalue);
  await engineStore.syncPidDraftEngine();
  await engineStore.getEngineConfig(tvalue);
};

export const handleNoPermissionCase = (
  hasViewPermission: boolean,
  publishedConfig,
  setInfoModalOpen: (value: boolean) => void,
): boolean => {
  if (!hasViewPermission && isEmpty(publishedConfig)) {
    setInfoModalOpen(true);
    return false;
  }
  return true;
};

export const setInitialTab = (
  engineStore: EngineStore,
  tvalue: (value: string) => string | number,
  setActiveTab: (tab: string) => void,
) => {
  if (engineStore.hasFinishedWizard) {
    if (!isEmpty(engineStore.publishedEngineConfig)) {
      setActiveTab(EngineConfigStatus.Published);
      engineStore.setEngineConfig(engineStore.publishedEngineConfig, tvalue);
    } else {
      setActiveTab(engineStore.configurationStatus);
    }
  } else {
    setActiveTab(versionTabs.engine);
  }
};

export const handleEngineSupport = (engineStore: EngineStore, stationId: string, navigate: (path: string) => void) => {
  if (engineStore.engineSupport) {
    const route = engineStore.isCentrifugalOnlyStation
      ? `/home/station/${stationId}/centrifugal`
      : `/home/station/${stationId}/engine`;
    navigate(route);
  }
};

export const handleTabConfig = (activeTab: string, engineStore: EngineStore, tvalue) => {
  const tabConfigs = {
    [EngineConfigStatus.Published]: () => {
      engineStore.setEngineConfig(engineStore.publishedEngineConfig, tvalue);
    },
    [EngineConfigStatus.Draft]: () => {
      engineStore.setEngineConfig(engineStore.draftEngineConfig, tvalue);
    },
    [EngineConfigStatus.Recommendation]: () => {
      engineStore.setEngineConfig(engineStore.automaticEngineConfig, tvalue);
    },
  };

  const configHandler = tabConfigs[activeTab];
  if (configHandler) {
    configHandler();
  }
};

export const handleTabChangeInEnginePage = async (
  tab: string,
  engineStore: EngineStore,
  tvalue,
  hasViewPermission: boolean,
  hasUpdatePermission: boolean,
  setActiveTab: (tab: string) => void,
) => {
  if (!hasViewPermission) return;

  const tabHandlers: Record<string, TabHandlerConfig> = {
    [versionTabs.draft]: {
      condition:
        isNil(engineStore.draftEngineConfig) && !isEmpty(engineStore.publishedEngineConfig) && hasUpdatePermission,
      action: async () => await engineStore.createNewDraft(tvalue),
    },
    [versionTabs.recommendation]: {
      condition: !window.localStorage.getItem('hasVisitedRecommendedTab'),
      action: async () => {
        window.localStorage.setItem('hasVisitedRecommendedTab', 'true');
        await engineStore.viewRecommendation();
      },
    },
    [versionTabs.versionHistory]: {
      condition: true,
      action: async () => engineStore.getVersionHistory(),
    },
    [versionTabs.engine]: {
      condition: true,
      action: async () => await engineStore.getEngineConfig(tvalue),
    },
  };

  const handler = tabHandlers[tab];
  if (handler && handler.condition) {
    handler.action();
  }

  setActiveTab(tab);
};

export const getTabTitleEnginePage = (engineConfigObj, publishedEngineConfig) => {
  if (engineConfigObj?.configurationStatus === EngineConfigStatus.Published) {
    return 'published';
  }
  if (
    engineConfigObj?.configurationStatus === EngineConfigStatus.Draft ||
    engineConfigObj?.configurationStatus === EngineConfigStatus.DefaultStrategy
  ) {
    return isEmpty(publishedEngineConfig) ? 'newDraft' : 'draft';
  }
  if (engineConfigObj?.configurationStatus === EngineConfigStatus.Recommendation) {
    return 'recommended';
  }
  return 'createNewDraft';
};
